// NOTE: Components loaded with this strategy DO NOT have access
// to in-component guards, such as beforeRouteEnter,
// beforeRouteUpdate, and beforeRouteLeave. You must either use
// route-level guards instead or lazy-load the component directly:
//
// component: () => import('@views/my-view')
//
import { isChunkLoadError, handleChunkError } from '@utils/chunkErrorHandler'

/**
 * ★ `lazyLoadView` para DUAS OU MAIS ROTAS que precisam compartilhar a mesma
 * instância montada.
 *
 * O problema que ele resolve (medido em 20/09/2026, spec-009): o vue-router
 * resolve o componente de cada rota no `beforeResolve`, e o teste dele é
 * `typeof def === 'function' && def.cid === undefined` — uma factory nunca
 * ganha `cid`, então ela é **chamada de novo a cada RouteRecord**. Duas rotas
 * declaradas com `component: () => lazyLoadView(import(...))` chamam
 * `lazyLoadView` duas vezes, e cada chamada fabrica o seu próprio
 * `AsyncHandler` — que, na rota nova, **ainda não resolveu**.
 *
 * ★ E é ISSO que destrói a instância, não a identidade do componente. O
 * primeiro render depois da navegação devolve um **placeholder de async**:
 * `createAsyncPlaceholder` monta um vnode de COMENTÁRIO, sem `tag` e sem
 * `key`. O `sameVnode` do Vue 2.6 só perdoa placeholder quando o vnode
 * **ANTIGO** é o placeholder (`isTrue(a.isAsyncPlaceholder)`); aqui o antigo é
 * um componente montado, então nenhum ramo casa e o diff destrói antes mesmo
 * de o render resolvido chegar.
 *
 * ⚠️ O `Ctor` NÃO diverge, e supor que sim manda o próximo dev para o conserto
 * errado. Medido: a tag é `vue-component-<cid>-TelaImersiva` **idêntica** dos
 * dois lados, porque `ensureCtor` estende o `default` do módulo e `Vue.extend`
 * cacheia em `comp._Ctor` — mesmo módulo, mesmo `Ctor`. E o wrapper
 * `{ functional, render }` é functional: nunca gera vnode próprio, então o
 * `cid` dele não chega a tag nenhuma.
 *
 * **O invariante a levar adiante:** o componente precisa já estar RESOLVIDO
 * quando o segundo RouteRecord renderizar pela primeira vez. Memoizar a
 * promessa é o que garante isso — a segunda rota nasce com `factory.resolved`
 * preenchido, o vnode resolvido entra já no primeiro tick, e o patch acontece
 * no lugar em vez de destruir.
 *
 * ⚠️ Os exemplos abaixo NÃO usam `import(` com string literal, e isso é
 * deliberado: o `--findRelatedTests` do Jest (que o CI roda no diff) faz
 * análise estática e trata `import('...')` como dependência REAL — mesmo
 * dentro de comentário ou de string. Um caminho de exemplo vira
 * `Configuration error: Could not locate module @modules/.../view.vue`, e o
 * job morre antes de rodar teste nenhum. Medido em 20/09/2026.
 *
 * @param {Function} importarModulo função que devolve a promessa do módulo —
 *   `() => import(<caminho da view>)`, com o import() escrito no call site
 * @returns {Function} a factory a passar em `component:` de CADA rota
 */
export const lazyLoadViewOnce = (importarModulo) => {
    // ⚠️ O irmão `lazyLoadView` recebe a PROMESSA; este recebe a FUNÇÃO que a
    // cria (é o que permite memoizar sem avaliar o `import()` no boot). Quem
    // espelhar o vizinho escreve `lazyLoadViewOnce(import('...'))` e, sem esta
    // guarda, colhe `importarModulo is not a function` de dentro do
    // `try/catch` do vue-router: a navegação aborta sem mensagem útil.
    if (typeof importarModulo !== 'function') {
        throw new TypeError(
            'lazyLoadViewOnce espera uma FUNÇÃO que devolva o import() — ' +
            `recebeu ${typeof importarModulo}. ` +
            'Use: lazyLoadViewOnce(() => import(<caminho da view>))'
        )
    }
    let promessa = null
    return () => {
        if (!promessa) {
            const moduloEmVoo = importarModulo()
            // ⚠️ Memoizar sem isto TROCARIA um defeito por outro: uma falha de
            // rede no chunk ficaria gravada na sessão inteira. Sem memoização,
            // cada navegação criava um `import()` novo — ou seja, uma nova
            // tentativa; e `handleChunkError` NÃO recarrega a página sozinho
            // (desde o fix do AAWZ-WEB-3VT ele só abre o modal "Nova versão", e
            // quem decide é o usuário), enquanto o `SafeAsyncView` devolve uma
            // promessa que nunca assenta. A tela ficaria pendurada até um reload
            // manual. Soltando a memoização no erro, a próxima navegação tenta de
            // novo, como antes; o caminho de SUCESSO — que é o que precisa da
            // identidade estável — não muda.
            moduloEmVoo.catch(() => {
                promessa = null
            })
            promessa = lazyLoadView(moduloEmVoo)
        }
        return promessa
    }
}

export const lazyLoadView = (AsyncView) => {
    const SafeAsyncView = typeof AsyncView.catch === 'function'
        ? AsyncView.catch((error) => {
            if (isChunkLoadError(error)) {
                handleChunkError()
                return new Promise(() => {})
            }
            throw error
        })
        : AsyncView

    const AsyncHandler = () => ({
        component: SafeAsyncView,
        loading: require('@views/_loading').default,
        delay: 400, // Delay antes de apresentar
        error: require('@views/_404').default, // Caso estoure o timeout
        // timeout: 10000,
    })

    return Promise.resolve({
        functional: true,
        render(h, { data, children }) {
            return h(AsyncHandler, data, children)
        },
    })
}
